import { useCallback, useEffect, useMemo, useState } from "react";
import { AlertTriangle, Coffee, Filter, RefreshCw, Search, X } from "lucide-react";
import AdminNavbar from "../../components/layout/AdminNavbar";
import api from "../../api/axios";

const LIMIT_MINUTES = 45;

const todayKey = () => {
    const now = new Date();
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, "0");
    const day = String(now.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
};

const formatTime = (value) => {
    if (!value) return "-";
    return new Date(value).toLocaleTimeString("en-IN", { timeZone: "Asia/Kolkata", hour: "2-digit", minute: "2-digit" });
};

const formatDate = (value) => {
    if (!value) return "-";
    return new Date(`${value}T12:00:00`).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
};

const getLunchBreak = (record) => {
    const lunchBreak = (record.breaks || []).find((item) => item?.type === "lunch" || (!item?.type && item?.startTime));
    return {
        start: record.breakStart || lunchBreak?.startTime,
        end: record.breakEnd || lunchBreak?.endTime,
    };
};

const minutesBetween = (start, end = new Date()) => {
    const startMs = new Date(start).getTime();
    const endMs = new Date(end).getTime();
    if (!Number.isFinite(startMs) || !Number.isFinite(endMs) || endMs <= startMs) return 0;
    return Math.floor((endMs - startMs) / 60000);
};

const buildFallbackAlerts = (records = [], selectedStatus = "active") => {
    const alerts = records
        .map((record) => {
            const lunch = getLunchBreak(record);
            const isRunning = Boolean(lunch.start && !lunch.end && !record.checkOut);
            const minutes = isRunning
                ? minutesBetween(lunch.start)
                : Math.round(Number(record.breakDuration || minutesBetween(lunch.start, lunch.end) || 0));
            const alertStatus = isRunning ? "active" : "resolved";

            if (!lunch.start || minutes <= LIMIT_MINUTES) return null;

            return {
                _id: `attendance-${record._id}`,
                employee: record.employee,
                attendance: record._id,
                date: record.date,
                team: record.employee?.team || record.team || "",
                lunchStart: lunch.start,
                lunchEnd: lunch.end,
                accumulatedMs: isRunning ? 0 : minutes * 60000,
                activeStart: isRunning ? lunch.start : null,
                minutes,
                limitMinutes: LIMIT_MINUTES,
                status: alertStatus,
                triggeredAt: lunch.end || lunch.start,
            };
        })
        .filter(Boolean);

    if (selectedStatus === "all") return alerts;
    return alerts.filter((alert) => alert.status === selectedStatus);
};

const LunchAlerts = () => {
    const [alerts, setAlerts] = useState([]);
    const [scope, setScope] = useState("today");
    const [search, setSearch] = useState("");
    const [teamFilter, setTeamFilter] = useState("all");
    const [dateFilter, setDateFilter] = useState(() => todayKey());
    const [minMinutes, setMinMinutes] = useState(LIMIT_MINUTES);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");
    const [now, setNow] = useState(Date.now());

    useEffect(() => {
        document.title = "Lunch Alerts | Savrion Technologies";
        const id = window.setInterval(() => setNow(Date.now()), 1000);
        return () => window.clearInterval(id);
    }, []);

    const teamOptions = useMemo(() => (
        [...new Set(alerts.map((alert) => alert.team || alert.employee?.team).filter(Boolean))]
            .sort((a, b) => String(a).localeCompare(String(b), undefined, { sensitivity: "base" }))
    ), [alerts]);

    const filteredAlerts = useMemo(() => {
        const query = search.trim().toLowerCase();
        const minimum = Math.max(LIMIT_MINUTES, Number(minMinutes || LIMIT_MINUTES));

        return alerts.map(alert => {
            let liveMinutes = alert.minutes || 0;
            if (alert.status === "active" && alert.activeStart) {
                const liveMs = (alert.accumulatedMs || 0) + (now - new Date(alert.activeStart).getTime());
                liveMinutes = Math.floor(Math.max(0, liveMs) / 60000);
            }
            return { ...alert, liveMinutes };
        }).filter((alert) => {
            const minutes = alert.liveMinutes;
            const employeeName = String(alert.employee?.name || "").toLowerCase();
            const employeeEmail = String(alert.employee?.email || "").toLowerCase();
            const employeeId = String(alert.employee?.employeeId || "").toLowerCase();
            const team = String(alert.team || alert.employee?.team || "");

            const matchesExceeded = minutes > LIMIT_MINUTES && minutes >= minimum;
            const matchesSearch = !query
                || employeeName.includes(query)
                || employeeEmail.includes(query)
                || employeeId.includes(query);
            const matchesTeam = teamFilter === "all" || team === teamFilter;
            const matchesDate = !dateFilter || alert.date === dateFilter;

            return matchesExceeded && matchesSearch && matchesTeam && matchesDate;
        });
    }, [alerts, dateFilter, minMinutes, search, teamFilter, now]);

    const stats = useMemo(() => ({
        maxMinutes: filteredAlerts.reduce((max, alert) => Math.max(max, Math.round(Number(alert.liveMinutes || 0))), 0),
    }), [filteredAlerts]);

    const resetFilters = () => {
        setSearch("");
        setTeamFilter("all");
        setDateFilter(todayKey());
        setMinMinutes(LIMIT_MINUTES);
        setScope("today");
    };

    const fetchAlerts = useCallback(async () => {
        setLoading(true);
        setError("");
        const scopedDate = scope === "today" ? todayKey() : dateFilter;
        if (scope === "today" && dateFilter !== scopedDate) {
            setDateFilter(scopedDate);
        }
        try {
            const res = await api.get("/api/admin/lunch-alerts", {
                cacheTtlMs: 30000,
                params: { status: "all", date: scopedDate || undefined },
            });
            setAlerts(Array.isArray(res.data) ? res.data : []);
        } catch (err) {
            if (err.response?.status === 404) {
                try {
                    const fallbackRes = await api.get("/api/admin/attendance", {
                        cacheTtlMs: 30000,
                        params: { date: scopedDate || undefined },
                    });
                    setAlerts(buildFallbackAlerts(Array.isArray(fallbackRes.data) ? fallbackRes.data : [], "all"));
                    setError("");
                    return;
                } catch (fallbackErr) {
                    const fallbackStatus = fallbackErr.response?.status;
                    const fallbackMessage = fallbackErr.response?.data?.message || fallbackErr.response?.data || fallbackErr.message || "Attendance fallback failed";
                    setError(fallbackStatus ? `Failed to load lunch alerts (${fallbackStatus}): ${fallbackMessage}` : `Failed to load lunch alerts: ${fallbackMessage}`);
                    return;
                }
            }
            const statusCode = err.response?.status;
            const message = err.response?.data?.message || err.response?.data || err.message || "Failed to load lunch alerts";
            setError(statusCode ? `Failed to load lunch alerts (${statusCode}): ${message}` : `Failed to load lunch alerts: ${message}`);
        } finally {
            setLoading(false);
        }
    }, [dateFilter, scope]);

    useEffect(() => {
        document.title = "Lunch Alerts | Savrion Technologies";
        fetchAlerts();
        const id = window.setInterval(fetchAlerts, 10000);
        return () => window.clearInterval(id);
    }, [fetchAlerts]);

    return (
        <div className="flex min-h-screen flex-col bg-[var(--bg-primary)] text-[var(--text-primary)] xl:flex-row">
            <AdminNavbar />
            <main className="min-w-0 flex-1 space-y-5 p-4 sm:p-6">
                <section className="rounded-2xl border border-[var(--border-color)] bg-[var(--card-bg)] p-5 shadow-[var(--shadow-soft)]">
                    <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
                        <div className="flex items-start gap-4">
                            <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-amber-500 text-white">
                                <Coffee size={22} />
                            </div>
                            <div>
                                <p className="text-xs font-black uppercase tracking-wide text-amber-500">Lunch Monitoring</p>
                                <h1 className="text-2xl font-black sm:text-3xl">Lunch Break Alerts</h1>
                                <p className="mt-1 text-sm text-[var(--text-secondary)]">Allowed lunch duration is {LIMIT_MINUTES} minutes.</p>
                            </div>
                        </div>
                        <div className="flex flex-wrap items-center gap-2">
                            {[
                                { value: "today", label: "Today Lunch" },
                                { value: "all", label: "All" },
                            ].map((item) => (
                                <button
                                    key={item.value}
                                    type="button"
                                    onClick={() => {
                                        setScope(item.value);
                                        if (item.value === "today") setDateFilter(todayKey());
                                    }}
                                    className={`h-10 rounded-xl border px-4 text-sm font-bold transition-all ${scope === item.value ? "border-amber-500 bg-amber-500 text-white" : "border-[var(--border-color)] bg-[var(--bg-primary)] text-[var(--text-secondary)]"}`}
                                >
                                    {item.label}
                                </button>
                            ))}

                        </div>
                    </div>
                </section>

                <section className="grid gap-3 sm:grid-cols-3">
                    <Metric label="Total exceeded" value={filteredAlerts.length} tone="text-red-500" />
                    <Metric label="Today exceeded" value={filteredAlerts.filter((alert) => alert.date === todayKey()).length} tone="text-emerald-500" />
                    <Metric label="Highest lunch" value={`${stats.maxMinutes || 0}m`} tone="text-amber-500" />
                </section>

                <section className="rounded-2xl border border-[var(--border-color)] bg-[var(--card-bg)] p-4 shadow-[var(--shadow-soft)]">
                    <div className="mb-3 flex items-center gap-2 text-sm font-black text-[var(--text-primary)]">
                        <Filter size={16} className="text-amber-500" />
                        Filter exceeded lunch records
                    </div>
                    <div className="grid gap-3 md:grid-cols-[minmax(220px,1fr)_180px_170px_150px_auto]">
                        <label className="relative">
                            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--text-secondary)]" />
                            <input
                                type="search"
                                value={search}
                                onChange={(event) => setSearch(event.target.value)}
                                placeholder="Search employee, email, ID"
                                className="h-11 w-full rounded-xl border border-[var(--border-color)] bg-[var(--bg-primary)] pl-9 pr-3 text-sm font-semibold outline-none focus:border-amber-500"
                            />
                        </label>
                        <select
                            value={teamFilter}
                            onChange={(event) => setTeamFilter(event.target.value)}
                            className="h-11 rounded-xl border border-[var(--border-color)] bg-[var(--bg-primary)] px-3 text-sm font-semibold outline-none focus:border-amber-500"
                        >
                            <option value="all">All teams</option>
                            {teamOptions.map((team) => (
                                <option key={team} value={team}>{team}</option>
                            ))}
                        </select>
                        <input
                            type="date"
                            value={dateFilter}
                            onChange={(event) => {
                                setDateFilter(event.target.value);
                                setScope("all");
                            }}
                            className="h-11 rounded-xl border border-[var(--border-color)] bg-[var(--bg-primary)] px-3 text-sm font-semibold outline-none focus:border-amber-500"
                        />
                        <label className="flex h-11 items-center gap-2 rounded-xl border border-[var(--border-color)] bg-[var(--bg-primary)] px-3">
                            <span className="shrink-0 text-xs font-black text-[var(--text-secondary)]">Min</span>
                            <input
                                type="number"
                                min={LIMIT_MINUTES}
                                value={minMinutes}
                                onChange={(event) => setMinMinutes(Math.max(LIMIT_MINUTES, Number(event.target.value || LIMIT_MINUTES)))}
                                className="min-w-0 flex-1 bg-transparent text-sm font-black outline-none"
                            />
                            <span className="text-xs font-black text-[var(--text-secondary)]">m</span>
                        </label>
                        <button
                            type="button"
                            onClick={resetFilters}
                            className="flex h-11 items-center justify-center gap-2 rounded-xl border border-[var(--border-color)] bg-[var(--bg-primary)] px-4 text-sm font-black text-[var(--text-secondary)] hover:bg-[var(--bg-secondary)]"
                        >
                            <X size={16} />
                            Clear
                        </button>
                    </div>
                    <p className="mt-3 text-xs font-semibold text-[var(--text-secondary)]">
                        Showing {filteredAlerts.length} employee lunch record{filteredAlerts.length === 1 ? "" : "s"} over {LIMIT_MINUTES} minutes
                        {dateFilter ? ` for ${formatDate(dateFilter)}.` : "."}
                    </p>
                </section>

                {error && (
                    <div className="rounded-xl border border-red-500/20 bg-red-500/10 px-4 py-3 text-sm font-semibold text-red-500">
                        {error}
                    </div>
                )}

                <section className="overflow-hidden rounded-2xl border border-[var(--border-color)] bg-[var(--card-bg)] shadow-[var(--shadow-soft)]">
                    <div className="overflow-x-auto">
                        <table className="w-full min-w-[780px] text-left text-sm">
                            <thead className="border-b border-[var(--border-color)] bg-[var(--bg-secondary)] text-xs uppercase text-[var(--text-secondary)]">
                                <tr>
                                    <th className="px-4 py-3">Employee</th>
                                    <th className="px-4 py-3">Team</th>
                                    <th className="px-4 py-3">Date</th>
                                    <th className="px-4 py-3">Lunch Start</th>
                                    <th className="px-4 py-3"><span className="whitespace-nowrap">Lunch End</span></th>
                                    <th className="px-4 py-3">Duration</th>
                                </tr>
                            </thead>
                            <tbody>
                                {!filteredAlerts.length && (
                                    <tr>
                                        <td colSpan="6" className="px-4 py-10 text-center text-[var(--text-secondary)]">
                                            {loading ? "Loading lunch alerts..." : "No employees exceeded the selected lunch filters."}
                                        </td>
                                    </tr>
                                )}
                                {filteredAlerts.map((alert) => (
                                    <tr key={alert._id} className="border-b border-[var(--border-color)] last:border-0">
                                        <td className="px-4 py-3">
                                            <div className="font-black">{alert.employee?.name || "Employee"}</div>
                                            <div className="text-xs text-[var(--text-secondary)]">{alert.employee?.employeeId || alert.employee?.email || "-"}</div>
                                        </td>
                                        <td className="px-4 py-3">{alert.team || alert.employee?.team || "-"}</td>
                                        <td className="px-4 py-3">{formatDate(alert.date)}</td>
                                        <td className="px-4 py-3">{formatTime(alert.lunchStart)}</td>
                                        <td className="px-4 py-3">{alert.status === "active" ? <span className="font-bold text-amber-600">Ongoing</span> : formatTime(alert.lunchEnd)}</td>
                                        <td className="px-4 py-3">
                                            <span className="inline-flex items-center gap-1 rounded-full bg-amber-500/10 px-2.5 py-1 text-xs font-black text-amber-600">
                                                <AlertTriangle size={13} />
                                                {Math.round(alert.liveMinutes || 0)}m{alert.status === "active" ? " ongoing" : ""}
                                            </span>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </section>
            </main>
        </div>
    );
};

const Metric = ({ label, value, tone }) => (
    <div className="rounded-2xl border border-[var(--border-color)] bg-[var(--card-bg)] p-4 shadow-[var(--shadow-soft)]">
        <p className="text-xs font-black uppercase text-[var(--text-secondary)]">{label}</p>
        <p className={`mt-1 text-2xl font-black ${tone}`}>{value}</p>
    </div>
);

export default LunchAlerts;


